// MeetingDetail.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import "./MeetingDetail.scss";

const MS_TRANSCRIPT_API =
  "https://meetinghub.insync-ai-suite.com/team/api/transcript";
const MS_MOM_API = "https://meetinghub.insync-ai-suite.com/team/api/mom/event";
const MS_GENERATE_MOM_API =
  "https://meetinghub.insync-ai-suite.com/team/api/mom/generate";

const GOOGLE_MEETINGS_API =
  "https://meetinghub.insync-ai-suite.com/api/v1/meetings";
const GOOGLE_TRANSCRIPT_PULL_API =
  "https://meetinghub.insync-ai-suite.com/api/v1/meetings";
const GOOGLE_REGENERATE_MOM_API =
  "https://meetinghub.insync-ai-suite.com/api/v1/meetings";

function MeetingDetail({ meeting, onBack, onRefresh, showNotification }) {
  const [detailedMeeting, setDetailedMeeting] = useState(meeting);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    const fetchDetailedData = async () => {
      setLoading(true);
      try {
        if (meeting.provider === "MICROSOFT") {
          const [transcriptRes, momRes] = await Promise.all([
            axios.get(`${MS_TRANSCRIPT_API}/${meeting.eventId}`),
            axios
              .get(`${MS_MOM_API}/${meeting._id}`)
              .catch(() => ({ data: null })),
          ]);

          setDetailedMeeting({
            ...meeting,
            transcript: transcriptRes.data.data,
            mom: momRes.data?.data,
          });
        } else if (meeting.provider === "GOOGLE") {
          const detailRes = await axios.get(
            `${GOOGLE_MEETINGS_API}/${meeting._id}`,
          );
          setDetailedMeeting({
            ...meeting,
            transcript: detailRes.data.meeting?.meetingTranscript,
            mom: detailRes.data.meeting?.meetingMom,
          });
        }
      } catch (error) {
        console.error("Error fetching details:", error);
        showNotification("Failed to load meeting details", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchDetailedData();
  }, [meeting]);

  const handlePullTranscript = async () => {
    setProcessing(true);
    try {
      if (detailedMeeting.provider === "MICROSOFT") {
        await axios.post(MS_GENERATE_MOM_API, {
          eventId: detailedMeeting.eventId,
        });
        showNotification(
          "Transcript pull initiated for Microsoft meeting",
          "success",
        );
      } else {
        const response = await axios.post(
          `${GOOGLE_TRANSCRIPT_PULL_API}/${detailedMeeting._id}/transcript/pull`,
        );
        showNotification(response.data.message, "success");
      }

      setTimeout(() => {
        onRefresh();
        showNotification("Operation completed successfully", "success");
        setProcessing(false);
      }, 2000);
    } catch (error) {
      console.error("Error pulling transcript:", error);
      showNotification("Failed to pull transcript", "error");
      setProcessing(false);
    }
  };

  const handleRegenerateMOM = async () => {
    setProcessing(true);
    try {
      if (detailedMeeting.provider === "MICROSOFT") {
        await axios.post(MS_GENERATE_MOM_API, {
          eventId: detailedMeeting.eventId,
        });
      } else {
        await axios.post(
          `${GOOGLE_REGENERATE_MOM_API}/${detailedMeeting._id}/mom/regenerate`,
        );
      }

      showNotification("MOM regeneration started successfully", "success");
      setTimeout(() => {
        onRefresh();
        showNotification("MOM regenerated successfully", "success");
        setProcessing(false);
      }, 2000);
    } catch (error) {
      console.error("Error regenerating MOM:", error);
      showNotification("Failed to regenerate MOM", "error");
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="meeting-detail-page">
        <div className="detail-loading">
          <div className="spinner"></div>
          <p>Loading meeting details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="meeting-detail-page">
      <div className="detail-nav">
        <button className="back-button" onClick={onBack}>
          <span className="arrow">←</span>
          Back to Meeting
        </button>
      </div>

      <div className="detail-container">
        <div className="detail-card">
          <div className="detail-card-header">
            <div>
              <h1 className="meeting-title">
                {detailedMeeting.subject || "Untitled Meeting"}
              </h1>
              <div className="meeting-meta">
                <span
                  className={`provider-badge provider-${detailedMeeting.provider.toLowerCase()}`}
                >
                  {detailedMeeting.provider}
                </span>
                <span className="status-badge">{detailedMeeting.status}</span>
              </div>
            </div>
          </div>

          <div className="info-section">
            <div className="info-grid">
              <div className="info-item">
                <label>Organizer</label>
                <div className="info-value">
                  {detailedMeeting.organizerEmail}
                </div>
              </div>
              <div className="info-item">
                <label>Start Time</label>
                <div className="info-value">
                  {new Date(detailedMeeting.startTime).toLocaleString()}
                </div>
              </div>
              <div className="info-item">
                <label>End Time</label>
                <div className="info-value">
                  {new Date(detailedMeeting.endTime).toLocaleString()}
                </div>
              </div>
              {detailedMeeting.joinUrl && (
                <div className="info-item full-width">
                  <label>Join URL</label>
                  <div className="info-value">
                    <a
                      href={detailedMeeting.joinUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="join-link"
                    >
                      Join Meeting →
                    </a>
                  </div>
                </div>
              )}
            </div>

            <div className="action-buttons">
              <button
                className="Meeting-secondary-btn"
                onClick={handlePullTranscript}
                disabled={processing}
              >
                {processing ? (
                  <>
                    <span className="spinner-small"></span>
                    Processing...
                  </>
                ) : (
                  "Pull Transcript & MOM"
                )}
              </button>
              {/* <button
                className="btn-primary"
                onClick={handleRegenerateMOM}
                disabled={processing}
              >
                {processing ? (
                  <>
                    <span className="spinner-small"></span>
                    Processing...
                  </>
                ) : (
                  "Generate / Regenerate MOM"
                )}
              </button> */}
            </div>
          </div>

          {detailedMeeting.transcript && (
            <div className="detail-section">
              <h2>Transcript</h2>
              <div className="transcript-content">
                <pre>
                  {detailedMeeting.transcript.mergedText ||
                    detailedMeeting.transcript.plainText}
                </pre>
              </div>
            </div>
          )}

          {detailedMeeting.mom && (
            <div className="detail-section">
              <h2>Meeting Minutes (MOM)</h2>
              <div className="mom-content">
                <div className="mom-block">
                  <h3>Summary</h3>
                  <p>{detailedMeeting.mom.summary}</p>
                </div>

                <div className="mom-block">
                  <h3>Minutes</h3>
                  <p>{detailedMeeting.mom.minutes}</p>
                </div>

                {detailedMeeting.mom.decisions &&
                  detailedMeeting.mom.decisions.length > 0 && (
                    <div className="mom-block">
                      <h3>Decisions</h3>
                      <ul>
                        {detailedMeeting.mom.decisions.map((decision, idx) => (
                          <li key={idx}>{decision}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                {detailedMeeting.mom.actionItems &&
                  detailedMeeting.mom.actionItems.length > 0 && (
                    <div className="mom-block">
                      <h3>Action Items</h3>
                      <ul>
                        {detailedMeeting.mom.actionItems.map((item, idx) => (
                          <li key={idx}>
                            <strong>{item.task}</strong>
                            {item.owner && item.owner !== "null" && (
                              <span className="owner">
                                {" "}
                                - Owner: {item.owner}
                              </span>
                            )}
                            {item.dueDate && (
                              <span className="due-date">
                                {" "}
                                - Due:{" "}
                                {new Date(item.dueDate).toLocaleDateString()}
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                {detailedMeeting.mom.keyPoints &&
                  detailedMeeting.mom.keyPoints.length > 0 && (
                    <div className="mom-block">
                      <h3>Key Points</h3>
                      <ul>
                        {detailedMeeting.mom.keyPoints.map((point, idx) => (
                          <li key={idx}>{point}</li>
                        ))}
                      </ul>
                    </div>
                  )}
              </div>
            </div>
          )}

          {!detailedMeeting.transcript && !detailedMeeting.mom && (
            <div className="empty-state">
              <p>No transcript or minutes available for this meeting yet.</p>
              <p className="hint">
                Click "Pull Transcript" to start the process.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default MeetingDetail;
